import React, { useEffect, useMemo, useState } from "react"
import { Button, Container } from "reactstrap"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import avatar from "../../../assets/images/avatar.png"
import {
  useApproveIcdPaymentMutation,
  useLazyGetOfflinePaymentsQuery,
  useRejectIcdPaymentMutation,
} from "store/api/icds/icds.api"
import { formatAmount, getTableSortingHeaderIcon } from "helpers/common"
import ApprovePaymentModel from "./confirm/approvePaymentModel"
import RejectPaymentModel from "./confirm/rejectPaymentModel"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"
import moment from "moment"

const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
}

const Payments = ({ HeaderComponent }) => {
  const [getResults, { isFetching }] = useLazyGetOfflinePaymentsQuery()
  const [onApprove, { isLoading: isApproving }] = useApproveIcdPaymentMutation()
  const [onReject, { isLoading: isRejecting }] = useRejectIcdPaymentMutation()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [isToast, setIsToast] = useState(false)
  const [icdData, setIcdData] = useState()
  const [actionType, setActionType] = useState()
  const { hasPermission } = usePermission()

  //meta title
  document.title = "Payments - Prachay"

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    getList()
  }, [filters])

  const columns = useMemo(() => {
    const data = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Request ID"),
        accessorKey: "unique_code",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <>{row?.original?.icdsRequests?.unique_code}</>,
      },
      {
        header: ({ column }) =>
          getTableSortingHeaderIcon(column, "Date of Payment"),
        accessorKey: "date_of_payment",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <>
            {row?.original.date_of_payment
              ? moment(row?.original.date_of_payment).format("D MMM YYYY")
              : "Not Applicable"}
          </>
        ),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Name"),
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <DefaultAvatar
            avatar_url={row?.original.investor?.avatar_url}
            email={row?.original.investor?.email}
            name={row?.original.investor?.name}
          />
        ),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Amount (₹)"),
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <div>₹ {formatAmount(row?.original.amount)}</div>,
      },
      {
        header: "Payment Mode",
        accessorKey: "payment_mode",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => <>{row?.original?.payment_type}</>,
      },
      {
        header: "UTR Number",
        accessorKey: "itr",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {row?.original?.utr_no || row?.original?.order_id}
          </div>
        ),
      },
      {
        header: () => <div className="flex items-center gap-2">Status</div>,
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {row?.original.status === "PENDING" && (
              <div className={`badge under_review`}>
                <span></span> Pending
              </div>
            )}
            {row?.original.status === "SUCCESS" && (
              <div className={`badge approved`}>
                <span></span> Approved
              </div>
            )}
            {row?.original.status === "FAILED" && (
              <div className={`badge rejected`}>
                <span></span>{" "}
                {row?.original?.payment_type === "row?.original?.payment_type"
                  ? "Failed"
                  : "Rejected"}
              </div>
            )}
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex flex-column align-items-center gap-1 buttons">
            {row?.original.status === "PENDING" &&
              row?.original.payment_type === "OFFLINE" && (
                <>
                  <Button
                    type="button"
                    className="approve"
                    onClick={() => {
                      setIsToast(true)
                      setActionType("APPROVE")
                      setIcdData(row?.original)
                    }}
                    style={{
                      minWidth: "130px",
                      maxWidth: "130px",
                      padding: "6px 25px",
                      textAlign: "center",
                    }}
                  >
                    Approve
                  </Button>

                  <Button
                    type="button"
                    className="reject"
                    size="sm"
                    onClick={() => {
                      setIsToast(true)
                      setActionType("REJECT")
                      setIcdData(row?.original)
                    }}
                    style={{
                      minWidth: "130px",
                      maxWidth: "130px",
                      padding: "6px 25px",
                      textAlign: "center",
                    }}
                  >
                    Reject
                  </Button>
                </>
              )}
          </div>
        ),
      },
    ]

    const canAccess = hasPermission(PERMISSIONS.ICD_UPDATE)
    if (canAccess) return data
    return data.filter((item) => item.id !== "actions")
  }, [hasPermission])

  return (
    <React.Fragment>
      <TableContainer
        isLoading={isFetching}
        count={count}
        filters={filters}
        setFilters={setFilters}
        HeaderComponent={HeaderComponent}
        columns={columns}
        data={data}
        isGlobalFilter={true}
        isCustomPageSize={true}
        // isGlobalFilter={true}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />

      {icdData && isToast && actionType === "APPROVE" && (
        <ApprovePaymentModel
          open={isToast}
          data={icdData}
          setOpen={setIsToast}
          isLoading={isApproving}
          onClose={() => {
            setIsToast(false)
            setActionType("")
          }}
          onApprove={onApprove}
          onSuccess={() => {
            getList()
          }}
        />
      )}

      {icdData && isToast && actionType === "REJECT" && (
        <RejectPaymentModel
          open={isToast}
          data={icdData}
          setOpen={setIsToast}
          isLoading={isRejecting}
          onClose={() => {
            setIsToast(false)
            setActionType("")
          }}
          onReject={onReject}
          onSuccess={() => {
            getList()
          }}
        />
      )}
    </React.Fragment>
  )
}

export default Payments
